import React from 'react';
import { Download, FileText, Table2 } from 'lucide-react';
import { SectionHeader } from '../shared';
import {
  buildBillingRows,
  buildPlatformSummaryRows,
  buildTenantRegistryRows,
  buildUsageRows,
  exportPlatformExcel,
  exportPlatformPdf,
} from '../superAdminReports';
import type { SuperAdminContext } from '../types';

export function ReportsSection({ ctx }: { ctx: SuperAdminContext }) {
  const { orgs, analytics, dateLabel } = ctx;

  const cards = [
    {
      title: 'Platform executive summary',
      desc: 'KPI snapshot for board / investor reporting.',
      pdf: () => exportPlatformPdf('Platform Executive Summary', buildPlatformSummaryRows(analytics, dateLabel), dateLabel),
      excel: () => exportPlatformExcel('platform-executive-summary', buildPlatformSummaryRows(analytics, dateLabel)),
    },
    {
      title: 'Tenant registry',
      desc: 'Full tenant list with billing, holds, data keys, and WhatsApp usage.',
      pdf: () => exportPlatformPdf('Tenant Registry', buildTenantRegistryRows(orgs), dateLabel),
      excel: () => exportPlatformExcel('tenant-registry', buildTenantRegistryRows(orgs)),
    },
    {
      title: 'Billing status report',
      desc: 'Subscription state per organization for finance ops.',
      pdf: () => exportPlatformPdf('Billing Status Report', buildBillingRows(orgs), dateLabel),
      excel: () => exportPlatformExcel('billing-status', buildBillingRows(orgs)),
    },
    {
      title: 'Usage & API consumption',
      desc: 'Data footprint and WhatsApp API spend ranked by tenant.',
      pdf: () => exportPlatformPdf('Usage and API Report', buildUsageRows(orgs), dateLabel),
      excel: () => exportPlatformExcel('usage-api', buildUsageRows(orgs)),
    },
  ] as const;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Reports & exports"
        subtitle="Enterprise-grade exports with the selected date range stamped on every PDF and spreadsheet — ready for audits, investors, and ops reviews."
      />

      <div className="grid gap-4 md:grid-cols-2">
        {cards.map((card) => (
          <div key={card.title} className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                <FileText className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-zinc-900">{card.title}</h3>
                <p className="mt-1 text-sm text-zinc-600">{card.desc}</p>
                <p className="mt-2 text-xs font-medium text-zinc-500">Period: {dateLabel}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={card.pdf}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-bold text-zinc-900 hover:bg-zinc-100"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Export PDF
                  </button>
                  <button
                    type="button"
                    onClick={card.excel}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-900 hover:bg-emerald-100"
                  >
                    <Table2 className="h-3.5 w-3.5" />
                    Export Excel
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm text-zinc-600">
        <p className="font-medium text-zinc-800">Operator tip</p>
        <p className="mt-1">
          Change the reporting period at the top of the console before exporting. All metrics and WhatsApp counts
          will reflect that window automatically.
        </p>
      </div>
    </div>
  );
}
